import React, { useMemo, useState } from 'react';
import { CalendarDays, CheckCircle2, ChevronRight } from 'lucide-react';
import { parseLocalDateTime, formatDateMedium } from '../../utils/timeUtils';
import type { RoutyDatePickerArtifact } from '../../business/routy/routyArtifacts';

interface DatePickerCardProps {
  artifact: RoutyDatePickerArtifact;
  disabled?: boolean;
  onPick: (artifact: RoutyDatePickerArtifact, date: string) => void;
}

const DatePickerCard: React.FC<DatePickerCardProps> = ({ artifact, disabled, onPick }) => {
  const [picked, setPicked] = useState<string | null>(null);
  const [custom, setCustom] = useState<string>(artifact.defaultDate ?? '');
  const locked = disabled || picked !== null;

  const taken = useMemo(() => new Set(artifact.takenDates ?? []), [artifact.takenDates]);

  const choose = (date: string) => {
    if (!date || locked) return;
    setPicked(date);
    onPick(artifact, date);
  };

  return (
    <div className="ml-10 rounded-lg bg-surface border border-ink-200 shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
        <CalendarDays className="w-4 h-4 text-ink-500" />
        <span className="text-sm font-semibold text-ink-900">{artifact.prompt || 'Pick a day'}</span>
      </div>

      <div className="p-3 space-y-2.5">
        {(artifact.options ?? []).map((opt) => {
          const isPicked = picked === opt.date;
          const isTaken = taken.has(opt.date);
          const parsed = parseLocalDateTime(opt.date);
          return (
            <button
              key={opt.date}
              onClick={() => choose(opt.date)}
              disabled={locked}
              className={`w-full flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors disabled:cursor-not-allowed ${
                isPicked
                  ? 'border-emerald-300 ring-1 ring-emerald-200 bg-emerald-50/50'
                  : 'border-ink-200 bg-surface hover:border-ink-300 hover:bg-ink-50 disabled:opacity-60'
              }`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-ink-900 flex items-center gap-1.5">
                  {opt.label}
                  {isTaken && (
                    <span className="text-2xs font-medium text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
                      already booked
                    </span>
                  )}
                </p>
                <p className="text-xs text-ink-500">{opt.note || (parsed ? formatDateMedium(parsed) : opt.date)}</p>
              </div>
              {isPicked ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 text-ink-300 flex-shrink-0" />
              )}
            </button>
          );
        })}

        {/* Free calendar picker */}
        <div className="rounded-lg border border-dashed border-ink-300 p-2.5">
          <p className="mb-1.5 text-2xs font-medium uppercase tracking-wide text-ink-400">Or choose any day</p>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={custom}
              min={artifact.minDate}
              disabled={locked}
              onChange={(e) => setCustom(e.target.value)}
              className="flex-1 rounded-lg border border-ink-200 bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:opacity-60"
            />
            <button
              onClick={() => choose(custom)}
              disabled={locked || !custom}
              className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Use this day
            </button>
          </div>
        </div>
      </div>

      {picked && (
        <div className="px-3 pb-3 -mt-1 flex items-center justify-center gap-1.5 text-xs font-medium text-emerald-600">
          <CheckCircle2 className="w-4 h-4" />
          Sent to Routy
        </div>
      )}
    </div>
  );
};

export default DatePickerCard;
